import type React from "react";
import { createContext, useContext, useEffect, useMemo, useSyncExternalStore } from "react";
import { AnimationController, type AnimationDisplayState } from "./AnimationController";

interface AnimationContextValue extends AnimationDisplayState {
  controller: AnimationController;
}

const AnimationContext = createContext<AnimationContextValue | null>(null);

export const useAnimation = () => {
  const ctx = useContext(AnimationContext);
  if (!ctx) throw new Error("useAnimation must be used within AnimationProvider");
  return ctx;
};

interface AnimationProviderProps {
  children: React.ReactNode;
}

export const AnimationProvider: React.FC<AnimationProviderProps> = ({ children }) => {
  const controller = useMemo(() => new AnimationController(), []);
  const state = useSyncExternalStore(controller.subscribe, controller.getState);

  useEffect(() => {
    return () => controller.dispose();
  }, [controller]);

  const value = useMemo(
    () => ({
      ...state,
      controller,
    }),
    [state, controller],
  );

  return <AnimationContext.Provider value={value}>{children}</AnimationContext.Provider>;
};
